import { useLingui } from '@lingui/react/macro';
import { styled } from '@linaria/react';
import { isDefined } from 'app-shared/utils';
import { IconButton } from 'app-ui/components';
import { IconX } from 'app-ui/icon';
import { MOBILE_VIEWPORT, themeCssVariables, useTheme } from 'app-ui/theme';
import { useState } from 'react';

import { LAYOUT_Z_INDEX } from '@/ui/layout/constants/LayoutZIndex';
import { EdgeResizeHandle } from '@/ui/layout/resize-handle/components/EdgeResizeHandle';
import { SIDE_PANEL_WIDTH } from '@/ui/layout/side-panel/constants/SidePanelWidth';
import { SidePanelHotkeyEffect } from '@/ui/layout/side-panel/effect-components/SidePanelHotkeyEffect';
import { useCloseSidePanel } from '@/ui/layout/side-panel/hooks/useCloseSidePanel';
import { isSidePanelOpenState } from '@/ui/layout/side-panel/states/isSidePanelOpenState';
import { sidePanelContentState } from '@/ui/layout/side-panel/states/sidePanelContentState';
import { sidePanelOpenSequenceState } from '@/ui/layout/side-panel/states/sidePanelOpenSequenceState';
import { sidePanelWidthState } from '@/ui/layout/side-panel/states/sidePanelWidthState';
import { HotkeyLayerEffect } from '@/ui/utilities/hotkey/effect-components/HotkeyLayerEffect';
import { useAtomState } from '@/ui/utilities/state/jotai/hooks/useAtomState';
import { useAtomStateValue } from '@/ui/utilities/state/jotai/hooks/useAtomStateValue';

const StyledSidePanel = styled.aside<{ panelWidth: number }>`
  background: ${themeCssVariables.background.primary};
  box-sizing: border-box;
  flex: none;
  height: 100%;
  overflow: hidden;
  position: relative;
  transition: width calc(${themeCssVariables.animation.duration.normal} * 1s)
    ease;
  width: 0;

  &[data-open] {
    border-left: 1px solid ${themeCssVariables.border.color.medium};
    width: ${({ panelWidth }) => `${panelWidth}px`};
  }

  &[data-resizing] {
    transition: none;
  }

  @media (max-width: ${MOBILE_VIEWPORT}px) {
    border-left: none;
    bottom: 0;
    box-shadow: ${themeCssVariables.boxShadow.strong};
    position: fixed;
    right: 0;
    top: 0;
    transform: translateX(100%);
    transition: transform
      calc(${themeCssVariables.animation.duration.normal} * 1s) ease;
    width: 100%;
    z-index: ${LAYOUT_Z_INDEX.sidePanel};

    &[data-open] {
      border-left: none;
      transform: translateX(0);
      width: 100%;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// Keeps its full width while the outer panel animates, so the text doesn't
// reflow during the slide.
const StyledPanelContent = styled.div<{ panelWidth: number }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: ${({ panelWidth }) => `${panelWidth}px`};

  @media (max-width: ${MOBILE_VIEWPORT}px) {
    width: 100%;
  }
`;

const StyledHeader = styled.div`
  align-items: center;
  background: ${themeCssVariables.background.secondary};
  border-bottom: 1px solid ${themeCssVariables.border.color.medium};
  box-sizing: border-box;
  display: flex;
  flex: none;
  gap: ${themeCssVariables.spacing[2]};
  height: ${themeCssVariables.spacing[12]};
  padding: 0 ${themeCssVariables.spacing[2]} 0 ${themeCssVariables.spacing[4]};
`;

const StyledTitle = styled.h2`
  color: ${themeCssVariables.font.color.primary};
  flex: 1;
  font-size: ${themeCssVariables.font.size.md};
  font-weight: ${themeCssVariables.font.weight.semiBold};
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledBody = styled.div`
  box-sizing: border-box;
  color: ${themeCssVariables.font.color.primary};
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${themeCssVariables.spacing[4]};
`;

export const SidePanel = () => {
  const { t } = useLingui();
  const theme = useTheme();
  const isSidePanelOpen = useAtomStateValue(isSidePanelOpenState);
  const sidePanelContent = useAtomStateValue(sidePanelContentState);
  const sidePanelOpenSequence = useAtomStateValue(sidePanelOpenSequenceState);
  const [sidePanelWidth, setSidePanelWidth] = useAtomState(sidePanelWidthState);
  const { closeSidePanel } = useCloseSidePanel();
  const [isResizing, setIsResizing] = useState(false);

  const handleCloseClick = () => {
    closeSidePanel();
  };

  const handleResizeStart = () => {
    setIsResizing(true);
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
  };

  return (
    <StyledSidePanel
      aria-label={sidePanelContent?.title}
      aria-hidden={!isSidePanelOpen}
      panelWidth={sidePanelWidth}
      data-open={isSidePanelOpen || undefined}
      data-resizing={isResizing || undefined}
      inert={!isSidePanelOpen}
    >
      {isSidePanelOpen && (
        <>
          <HotkeyLayerEffect layer="side-panel" />
          <SidePanelHotkeyEffect />
        </>
      )}
      {isDefined(sidePanelContent) && (
        <StyledPanelContent panelWidth={sidePanelWidth}>
          <StyledHeader>
            <StyledTitle>{sidePanelContent.title}</StyledTitle>
            <IconButton
              variant="ghost"
              size="sm"
              aria-label={t`Close panel`}
              onClick={handleCloseClick}
            >
              <IconX size={theme.icon.size.md} stroke={theme.icon.stroke.sm} />
            </IconButton>
          </StyledHeader>
          <StyledBody key={sidePanelOpenSequence}>
            {sidePanelContent.content}
          </StyledBody>
        </StyledPanelContent>
      )}
      <EdgeResizeHandle
        edge="left"
        label={t`Resize panel`}
        width={sidePanelWidth}
        minimumWidth={SIDE_PANEL_WIDTH.min}
        maximumWidth={SIDE_PANEL_WIDTH.max}
        onWidthChange={setSidePanelWidth}
        onResizeStart={handleResizeStart}
        onResizeEnd={handleResizeEnd}
      />
    </StyledSidePanel>
  );
};
